import React from 'react';
import './css/flprivacypolicy.css';
import FLHeader from './FLHeader';

export default function FLPrivacyPolicy() {
    return (
        <div className="fl">
            
            <FLHeader subtitle="Privacy Policy"/> 

            <main className="fl__content">
                
                <p className="fl__updated">Last Updated: 21 August 2026</p>

                <div className="fl__policy">
                    <p>
                        FitnessLogger (&quot;FitnessLogger&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your
                        privacy and is committed to protecting the information you provide when using
                        the FitnessLogger mobile application (the &quot;App&quot;).
                    </p>
                    <p>
                        This Privacy Policy explains what information we collect, how we use it, how we
                        store it, and the choices available to you.
                    </p>
                    <p>
                        By using FitnessLogger, you agree to the practices described in this Privacy
                        Policy.
                    </p>

                    <section className="fl__section">
                        <h3 className="fl__section-title">1. Information We Collect</h3>

                        <h4 className="fl__section-subtitle">1.1 Workout and Training Data</h4>
                        <p>
                            FitnessLogger allows you to record information about your training
                            activities. Depending on the type of training you record, this may include:
                        </p>
                        <ul className="fl__list">
                            <li>Exercise names</li>
                            <li>Muscle groups</li>
                            <li>Sets and repetitions</li>
                            <li>Weight lifted</li>
                            <li>Equipment used</li>
                            <li>Training levels</li>
                            <li>Distance</li>
                            <li>Duration and time</li>
                            <li>Sports training metrics</li>
                            <li>Core training information</li>
                            <li>Workout dates</li>
                            <li>Notes that you choose to enter</li>
                        </ul>
                        <p>
                            This information is provided voluntarily by you and is used to provide the
                            core functionality of the App.
                        </p>

                        <h4 className="fl__section-subtitle">1.2 Account Information</h4>
                        <p>
                            To use FitnessLogger and access cloud storage features, an account is
                            required. We collect the information necessary to create and maintain your
                            account, including:
                        </p>
                        <ul className="fl__list">
                            <li>Email address</li>
                            <li>User/account identifier</li>
                            <li>Authentication information</li>
                        </ul>
                        <p>
                            We do not intentionally collect sensitive personal information unless it is
                            specifically required for a feature and clearly disclosed to you.
                        </p>

                        <h4 className="fl__section-subtitle">1.3 Device and Technical Information</h4>
                        <p>
                            We may automatically receive limited technical information required to
                            operate, secure, and improve the App. This may include:
                        </p>
                        <ul className="fl__list">
                            <li>Device type</li>
                            <li>Operating system and version</li>
                            <li>App version</li>
                            <li>Basic diagnostic information</li>
                            <li>Crash and error information</li>
                            <li>Anonymous or pseudonymous identifiers</li>
                        </ul>
                        <p>
                            The information collected depends on the technical services used to operate
                            FitnessLogger.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">2. How We Use Your Information</h3>
                        <p>We use information collected through FitnessLogger to:</p>
                        <ul className="fl__list">
                            <li>Provide and operate the App</li>
                            <li>Save and display your workout history</li>
                            <li>Synchronise your training information between supported devices</li>
                            <li>Generate progression charts</li>
                            <li>Display your training calendar</li>
                            <li>Maintain and improve the App</li>
                            <li>Diagnose technical problems</li>
                            <li>Prevent abuse, fraud, or unauthorised access</li>
                            <li>Respond to support requests</li>
                            <li>Maintain the security and reliability of our services</li>
                        </ul>
                        <p>We do not sell your personal information.</p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">3. Workout Data</h3>
                        <p>
                            Your workout information is stored so that you can access your training
                            history and use FitnessLogger&apos;s features.
                        </p>
                        <p>
                            FitnessLogger stores your workout information on our servers or with
                            third-party infrastructure providers that process data on our behalf.
                        </p>
                        <p>
                            We take reasonable technical and organisational measures to protect stored
                            information from unauthorised access, alteration, disclosure, or destruction.
                        </p>
                        <p>
                            However, no internet-based service can guarantee absolute security.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">4. Data Sharing</h3>
                        <p>We do not sell or rent your personal information to third parties.</p>
                        <p>
                            We may share limited information with service providers who help us operate
                            FitnessLogger, such as providers of:
                        </p>
                        <ul className="fl__list">
                            <li>Cloud/database hosting</li>
                            <li>Authentication</li>
                            <li>Crash reporting</li>
                            <li>Analytics</li>
                            <li>Infrastructure and security services</li>
                        </ul>
                        <p>
                            These providers are permitted to process information only as necessary to
                            provide their services to us.
                        </p>
                        <p>
                            We may also disclose information where required by law, legal process, or
                            where reasonably necessary to protect the rights, safety, or security of
                            FitnessLogger, our users, or others.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">5. Analytics and Advertising</h3>
                        <p>
                            FitnessLogger does not display advertisements and does not require users to
                            pay for additional features through advertising or advertising-related
                            access restrictions.
                        </p>
                        <p>
                            If we introduce analytics or other third-party services in the future that
                            collect additional personal or device information, this Privacy Policy will
                            be updated accordingly and users will be notified where required by
                            applicable law.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">6. Data Retention</h3>
                        <p>
                            We retain your information for as long as reasonably necessary to provide
                            FitnessLogger and its features, and for a period of up to 90 days following
                            account deletion to allow for backup processing and deletion procedures.
                        </p>
                        <p>
                            If you delete your account, we will delete or anonymise your associated
                            personal information within a reasonable period, except where we are legally
                            required or permitted to retain certain information.
                        </p>
                        <p>
                            Some information may remain temporarily in backups before being permanently
                            deleted in accordance with our backup and retention procedures.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">7. Your Rights and Choices</h3>
                        <p>
                            Depending on where you live, you may have rights regarding your personal
                            information. In particular, if you are located in the United Kingdom or
                            European Economic Area, you have rights under the UK GDPR and GDPR
                            respectively, including the right to:
                        </p>
                        <ul className="fl__list">
                            <li>Request access to your personal information</li>
                            <li>Request correction of inaccurate information</li>
                            <li>Request deletion of your information</li>
                            <li>Request restriction of certain processing</li>
                            <li>Object to certain uses of your information</li>
                            <li>Request a copy of certain information in a portable format</li>
                            <li>Withdraw consent where processing is based on consent</li>
                            <li>
                                Lodge a complaint with a supervisory authority (in the UK, the
                                Information Commissioner&apos;s Office — ico.org.uk)
                            </li>
                        </ul>
                        <p>
                            To exercise applicable rights, contact us using the details provided in the
                            Contact Us section.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">8. Children&apos;s Privacy</h3>
                        <p>
                            FitnessLogger is intended for users aged 13 and over. The App is not directed
                            at children under the age of 13, and we do not knowingly collect personal
                            information from children under this age without appropriate parental or
                            guardian consent.
                        </p>
                        <p>
                            If you believe that a child under 13 has provided us with personal
                            information without appropriate consent, please contact us so that we can
                            investigate and take appropriate action.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">9. Data Security</h3>
                        <p>
                            We use reasonable security measures designed to protect your information.
                            These measures may include:
                        </p>
                        <ul className="fl__list">
                            <li>Access controls and authentication mechanisms</li>
                            <li>Encryption where appropriate</li>
                            <li>Secure communications</li>
                            <li>Restricted access to personal information</li>
                        </ul>
                        <p>
                            Despite these measures, no method of electronic storage or transmission over
                            the internet is completely secure.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">10. International Data Transfers</h3>
                        <p>
                            Depending on the location of our service providers and infrastructure, your
                            information may be processed or stored in countries outside your country of
                            residence.
                        </p>
                        <p>
                            Where required by applicable law, we will take appropriate measures to ensure
                            that international transfers of personal information are adequately protected
                            in accordance with relevant data protection legislation.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">11. Third-Party Services</h3>
                        <p>
                            FitnessLogger may use third-party services to help provide, maintain, secure,
                            or improve the App. These services may have their own privacy policies
                            governing their handling of information.
                        </p>
                        <p>
                            Where applicable, users should review the privacy policies of relevant
                            third-party service providers.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">12. Changes to This Privacy Policy</h3>
                        <p>
                            We may update this Privacy Policy from time to time to reflect changes to
                            FitnessLogger, our services, technology, or applicable legal requirements.
                        </p>
                        <p>
                            When we make changes, we will update the &quot;Last Updated&quot; date at the
                            beginning of this Privacy Policy. Where required by law, we will provide more
                            prominent notice of material changes.
                        </p>
                        <p>
                            We encourage you to periodically review this Privacy Policy.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">13. Contact Us</h3>
                        <p>
                            If you have questions about this Privacy Policy, your personal information,
                            or your privacy rights, please contact us at:
                        </p>
                        <ul className="fl__list">
                            <li>Email: contact@solopixeldev.co.uk</li>
                            <li>App: FitnessLogger</li>
                            <li>Developer: Solo Pixel Dev</li>
                            <li>Website: solopixeldev.co.uk</li>
                        </ul>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">14. Consent</h3>
                        <p>
                            By using FitnessLogger, you acknowledge that you have read and understood
                            this Privacy Policy and agree to the collection and use of information as
                            described above, to the extent permitted by applicable law.
                        </p>
                    </section>
                </div>

                <hr className="fl__divider" />
            </main>
        </div>
    );
}
